import React, { useMemo, useState, useCallback } from 'react';
import { MdClose, MdOpenInNew } from 'react-icons/md';

import PropTypes from 'prop-types';

import {
    Dialog,
    Grid,
    IconButton,
    Link,
    Paper,
    Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import LocaleMessage from '~/components/LocaleMessage';

const useStyles = makeStyles(theme => ({
    section: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(1),
    },

    sectionLabel: {
        fontSize: '0.7rem',
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        fontWeight: 600,
        color: theme.palette.primary.main,
        marginBottom: theme.spacing(1.5),
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        '&::after': {
            content: '""',
            flex: 1,
            height: 1,
            background: `linear-gradient(90deg, ${theme.palette.primary.light} 0%, transparent 100%)`,
            opacity: 0.5,
        },
    },

    gallerySurface: {
        padding: theme.spacing(2),
        borderRadius: 16,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor:
            theme.palette.type === 'dark'
                ? 'rgba(255,255,255,0.03)'
                : theme.palette.grey[50],
        boxShadow:
            theme.palette.type === 'dark'
                ? 'none'
                : '0 1px 3px rgba(0,0,0,0.06)',
    },

    card: {
        position: 'relative',
        borderRadius: 12,
        overflow: 'hidden',
        cursor: 'pointer',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: theme.transitions.create(
            ['transform', 'box-shadow', 'border-color'],
            { duration: theme.transitions.duration.shorter }
        ),
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        boxShadow:
            theme.palette.type === 'dark'
                ? '0 2px 12px rgba(0,0,0,0.35)'
                : '0 2px 12px rgba(0,0,0,0.06)',
        '&:hover, &:focus-visible': {
            transform: 'translateY(-2px)',
            boxShadow:
                theme.palette.type === 'dark'
                    ? `0 8px 28px rgba(0,0,0,0.45), 0 0 0 1px ${theme.palette.primary.dark}`
                    : `0 12px 28px rgba(0,0,0,0.12), 0 0 0 1px ${theme.palette.primary.light}`,
            outline: 'none',
        },
    },
    cardBroken: {
        cursor: 'default',
        '&:hover, &:focus-visible': {
            transform: 'none',
            boxShadow:
                theme.palette.type === 'dark'
                    ? '0 2px 12px rgba(0,0,0,0.35)'
                    : '0 2px 12px rgba(0,0,0,0.06)',
            borderColor: theme.palette.divider,
        },
    },

    cardHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
        padding: theme.spacing(1.25, 1.5),
        borderBottom: `1px solid ${theme.palette.divider}`,
        background:
            theme.palette.type === 'dark'
                ? 'linear-gradient(180deg, rgba(255,255,255,0.06) 0%, transparent 100%)'
                : 'linear-gradient(180deg, #fff 0%, #fafbfc 100%)',
        flexShrink: 0,
    },
    cardHeaderTitle: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        flex: 1,
        minWidth: 0,
    },
    headerOpen: {
        color: theme.palette.primary.main,
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0,
        padding: theme.spacing(0.25),
        borderRadius: 8,
        transition: theme.transitions.create(['background-color', 'color'], {
            duration: theme.transitions.duration.shorter,
        }),
        '&:hover': {
            backgroundColor:
                theme.palette.type === 'dark'
                    ? 'rgba(255,255,255,0.08)'
                    : theme.palette.action.hover,
            color: theme.palette.primary.dark,
        },
    },

    imageWrap: {
        position: 'relative',
        width: '100%',
        aspectRatio: '4 / 3',
        [theme.breakpoints.up('sm')]: {
            aspectRatio: '16 / 10',
        },
        backgroundColor:
            theme.palette.type === 'dark'
                ? theme.palette.grey[900]
                : theme.palette.grey[100],
    },
    img: {
        display: 'block',
        position: 'absolute',
        left: 0,
        top: 0,
        width: '100%',
        height: '100%',
        objectFit: 'contain',
        objectPosition: 'center',
        zIndex: 1,
    },
    shimmer: {
        position: 'absolute',
        left: 0,
        top: 0,
        right: 0,
        bottom: 0,
        zIndex: 0,
        background:
            theme.palette.type === 'dark'
                ? `linear-gradient(110deg, ${theme.palette.grey[800]} 0%, ${theme.palette.grey[700]} 45%, ${theme.palette.grey[800]} 90%)`
                : `linear-gradient(110deg, ${theme.palette.grey[200]} 0%, ${theme.palette.grey[100]} 45%, ${theme.palette.grey[200]} 90%)`,
        animation: '$pulse 1.4s ease-in-out infinite',
    },
    '@keyframes pulse': {
        '0%, 100%': { opacity: 0.6 },
        '50%': { opacity: 1 },
    },
    titleAccent: {
        width: 3,
        height: 14,
        borderRadius: 2,
        flexShrink: 0,
        background: `linear-gradient(180deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
    },
    cardHeaderTitleText: {
        fontWeight: 600,
        fontSize: '0.8125rem',
        lineHeight: 1.35,
        color: theme.palette.text.primary,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        minWidth: 0,
    },
    tileCaption: {
        padding: theme.spacing(0.75, 1.5),
        textAlign: 'center',
        fontSize: '0.6875rem',
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: theme.palette.text.secondary,
        borderTop: `1px solid ${theme.palette.divider}`,
        backgroundColor:
            theme.palette.type === 'dark'
                ? 'rgba(0,0,0,0.2)'
                : theme.palette.grey[50],
    },
    broken: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'absolute',
        left: 0,
        top: 0,
        right: 0,
        bottom: 0,
        zIndex: 1,
        padding: theme.spacing(2),
        color: theme.palette.text.secondary,
        fontSize: '0.8125rem',
        backgroundColor:
            theme.palette.type === 'dark'
                ? theme.palette.grey[900]
                : theme.palette.grey[200],
    },
    lightboxPaper: {
        backgroundColor: 'rgba(18, 18, 22, 0.96)',
        boxShadow: '0 24px 80px rgba(0,0,0,0.45)',
        maxWidth: '96vw',
        maxHeight: '96vh',
        margin: theme.spacing(2),
        overflow: 'hidden',
        borderRadius: 16,
    },
    lightboxToolbar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(0.5, 0.5, 0.5, 2),
        color: '#fff',
    },
    lightboxImg: {
        display: 'block',
        maxWidth: '100%',
        maxHeight: 'calc(96vh - 120px)',
        width: 'auto',
        height: 'auto',
        margin: '0 auto',
        objectFit: 'contain',
    },
    lightboxFooter: {
        padding: theme.spacing(1, 2, 2),
        textAlign: 'center',
    },
}));

export const CLEANING_REPORT_MAP_SLOT_KEYS = {
    TASK_RESULT: 'task_result_url',
    CLEAN_EFFECT: 'clean_effect_url',
};

const MAP_SLOTS = [
    {
        key: CLEANING_REPORT_MAP_SLOT_KEYS.TASK_RESULT,
        labelMsg: 'page.robots.cleaning_report.detail.link.task_result',
    },
    {
        key: CLEANING_REPORT_MAP_SLOT_KEYS.CLEAN_EFFECT,
        labelMsg: 'page.robots.cleaning_report.detail.link.clean_effect',
    },
];

function isHttpUrl(v) {
    const s = v && String(v).trim();
    return s && /^https?:\/\//i.test(s);
}

export default function CleaningReportMapGallery({
    floor,
    slotKeys = null,
    hideSectionLabel = false,
    showEmptyPlaceholder = false,
}) {
    const classes = useStyles();
    const [loaded, setLoaded] = useState({});
    const [broken, setBroken] = useState({});
    const [lightbox, setLightbox] = useState(null);

    const items = useMemo(() => {
        if (!floor || typeof floor !== 'object') return [];
        const slots =
            Array.isArray(slotKeys) && slotKeys.length > 0
                ? MAP_SLOTS.filter(s => slotKeys.includes(s.key))
                : MAP_SLOTS;
        return slots
            .map(slot => {
                const url = floor[slot.key];
                if (!isHttpUrl(url)) return null;
                return {
                    ...slot,
                    url: String(url).trim(),
                };
            })
            .filter(Boolean);
    }, [floor, slotKeys]);

    const openLightbox = useCallback(item => {
        setLightbox(item);
    }, []);

    const closeLightbox = useCallback(() => {
        setLightbox(null);
    }, []);

    if (items.length === 0) {
        if (showEmptyPlaceholder) {
            return (
                <Typography
                    variant="body2"
                    color="textSecondary"
                    align="center"
                    style={{ padding: '2rem 1rem' }}
                >
                    <LocaleMessage msg="page.robots.cleaning_report.detail.maps.no_preview" />
                </Typography>
            );
        }
        return null;
    }

    const isPair = items.length === 2;

    return (
        <>
            <div className={classes.section}>
                {!hideSectionLabel ? (
                    <Typography
                        component="div"
                        className={classes.sectionLabel}
                        variant="overline"
                    >
                        <LocaleMessage msg="page.robots.cleaning_report.detail.maps.section_title" />
                    </Typography>
                ) : null}
                <div className={classes.gallerySurface}>
                    <Grid container spacing={2}>
                        {items.map(item => {
                            const id = item.key;
                            const isBroken = broken[id];
                            const isLoaded = loaded[id];
                            return (
                                <Grid
                                    item
                                    xs={12}
                                    sm={isPair ? 6 : 12}
                                    md={isPair ? 6 : 12}
                                    key={id}
                                >
                                    <Paper
                                        elevation={0}
                                        className={`${classes.card} ${
                                            isBroken ? classes.cardBroken : ''
                                        }`}
                                        onClick={
                                            isBroken
                                                ? undefined
                                                : () => openLightbox(item)
                                        }
                                        onKeyDown={e => {
                                            if (isBroken) return;
                                            if (
                                                e.key === 'Enter' ||
                                                e.key === ' '
                                            ) {
                                                e.preventDefault();
                                                openLightbox(item);
                                            }
                                        }}
                                        role={isBroken ? undefined : 'button'}
                                        tabIndex={isBroken ? -1 : 0}
                                    >
                                        <div className={classes.cardHeader}>
                                            <div
                                                className={
                                                    classes.cardHeaderTitle
                                                }
                                            >
                                                <span
                                                    className={
                                                        classes.titleAccent
                                                    }
                                                    aria-hidden
                                                />
                                                <Typography
                                                    component="span"
                                                    className={
                                                        classes.cardHeaderTitleText
                                                    }
                                                >
                                                    <LocaleMessage
                                                        msg={item.labelMsg}
                                                    />
                                                </Typography>
                                            </div>
                                            <Link
                                                href={item.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className={classes.headerOpen}
                                                onClick={e =>
                                                    e.stopPropagation()
                                                }
                                                aria-label="open-new-tab"
                                            >
                                                <MdOpenInNew size={20} />
                                            </Link>
                                        </div>
                                        <div className={classes.imageWrap}>
                                            {!isLoaded && !isBroken ? (
                                                <span
                                                    className={classes.shimmer}
                                                />
                                            ) : null}
                                            {isBroken ? (
                                                <div className={classes.broken}>
                                                    <LocaleMessage msg="page.robots.cleaning_report.detail.maps.load_error" />
                                                </div>
                                            ) : (
                                                <img
                                                    className={classes.img}
                                                    src={item.url}
                                                    alt=""
                                                    loading="lazy"
                                                    decoding="async"
                                                    onLoad={() =>
                                                        setLoaded(p => ({
                                                            ...p,
                                                            [id]: true,
                                                        }))
                                                    }
                                                    onError={() =>
                                                        setBroken(p => ({
                                                            ...p,
                                                            [id]: true,
                                                        }))
                                                    }
                                                />
                                            )}
                                        </div>
                                        {!isBroken ? (
                                            <div
                                                className={classes.tileCaption}
                                            >
                                                <LocaleMessage msg="page.robots.cleaning_report.detail.maps.expand_hint" />
                                            </div>
                                        ) : null}
                                    </Paper>
                                </Grid>
                            );
                        })}
                    </Grid>
                </div>
            </div>

            <Dialog
                open={Boolean(lightbox)}
                onClose={closeLightbox}
                maxWidth={false}
                PaperProps={{
                    className: classes.lightboxPaper,
                }}
            >
                {lightbox ? (
                    <>
                        <div className={classes.lightboxToolbar}>
                            <Typography
                                variant="subtitle2"
                                style={{ color: '#fff' }}
                            >
                                <LocaleMessage msg={lightbox.labelMsg} />
                            </Typography>
                            <IconButton
                                onClick={closeLightbox}
                                style={{ color: '#fff' }}
                                aria-label="close"
                            >
                                <MdClose size={24} />
                            </IconButton>
                        </div>
                        {!broken[lightbox.key] ? (
                            <img
                                className={classes.lightboxImg}
                                src={lightbox.url}
                                alt=""
                            />
                        ) : null}
                        <div className={classes.lightboxFooter}>
                            <Link
                                href={lightbox.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: '#90caf9' }}
                            >
                                <LocaleMessage msg="page.robots.cleaning_report.detail.maps.open_full" />{' '}
                                <MdOpenInNew
                                    size={16}
                                    style={{
                                        verticalAlign: 'middle',
                                        marginLeft: 4,
                                    }}
                                />
                            </Link>
                        </div>
                    </>
                ) : null}
            </Dialog>
        </>
    );
}

CleaningReportMapGallery.propTypes = {
    // eslint-disable-next-line react/forbid-prop-types -- payload Pudu por piso
    floor: PropTypes.object.isRequired,
    slotKeys: PropTypes.arrayOf(PropTypes.string),
    hideSectionLabel: PropTypes.bool,
    showEmptyPlaceholder: PropTypes.bool,
};

CleaningReportMapGallery.defaultProps = {
    slotKeys: null,
    hideSectionLabel: false,
    showEmptyPlaceholder: false,
};
